import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  X,
  BookOpen,
  Users,
  Calendar,
  User,
  GraduationCap,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  getClass,
  addStudentToClass,
  removeStudentFromClass,
  addSubjectToClass,
  assignTeacherToSubject,
  setClassTeacher,
  addPeriodToSchedule,
  getClassSchedule,
  bulkAssignStudents,
  bulkRemoveStudents,
} from "../../services/classes";
import { getStudents } from "../../services/students";
import { getTeachers } from "../../services/teachers";
import { getCourses } from "../../services/courses";

interface ClassManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId: string;
}

type TabType = "students" | "teachers" | "schedule" | "overview";

const ClassManagementModal: React.FC<ClassManagementModalProps> = ({
  isOpen,
  onClose,
  classId,
}) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [newSubject, setNewSubject] = useState({
    subjectId: "",
    teacherId: "",
  });
  const [newPeriod, setNewPeriod] = useState({
    day: "",
    startTime: "",
    endTime: "",
    subjectId: "",
    teacherId: "",
    studentIds: [] as string[], // empty = all students, non-empty = specific students
  });

  // Fetch class data
  const { data: classResponse, isLoading: classLoading } = useQuery({
    queryKey: ["class", classId],
    queryFn: () => getClass(classId),
    enabled: isOpen && !!classId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch students
  const { data: studentsResponse } = useQuery({
    queryKey: ["students"],
    queryFn: () => getStudents({ limit: 1000 }),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch teachers
  const { data: teachersResponse } = useQuery({
    queryKey: ["teachers"],
    queryFn: () => getTeachers({ limit: 1000 }),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch courses/subjects
  const { data: coursesResponse } = useQuery({
    queryKey: ["courses"],
    queryFn: () => getCourses({ limit: 1000 }),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch class schedule
  const { data: scheduleResponse } = useQuery({
    queryKey: ["class-schedule", classId],
    queryFn: () => getClassSchedule(classId),
    enabled: isOpen && activeTab === "schedule" && !!classId,
    staleTime: 2 * 60 * 1000,
  });

  const classData = classResponse?.class;
  const students = studentsResponse?.data || [];
  const teachers = teachersResponse?.data || [];
  const courses = coursesResponse?.data || [];
  const schedule = scheduleResponse?.data?.schedule || [];

  // Get students not in class
  const availableStudents = students.filter(
    (student) => !classData?.students?.some((s: any) => s._id === student._id),
  );

  // Add students to class mutation
  const addStudentsMutation = useMutation({
    mutationFn: async (studentIds: string[]) => {
      if (studentIds.length === 1) {
        return addStudentToClass(classId, { studentId: studentIds[0] });
      } else {
        return bulkAssignStudents(classId, studentIds);
      }
    },
    onSuccess: () => {
      toast.success("Students added to class successfully");
      queryClient.invalidateQueries({ queryKey: ["class", classId] });
      setSelectedStudents([]);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to add students to class");
    },
  });

  // Remove students from class mutation
  const removeStudentsMutation = useMutation({
    mutationFn: async (studentIds: string[]) => {
      if (studentIds.length === 1) {
        return removeStudentFromClass(classId, studentIds[0]);
      } else {
        return bulkRemoveStudents(classId, studentIds);
      }
    },
    onSuccess: () => {
      toast.success("Students removed from class successfully");
      queryClient.invalidateQueries({ queryKey: ["class", classId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to remove students from class");
    },
  });

  // Add subject to class mutation
  const addSubjectMutation = useMutation({
    mutationFn: async () => {
      return addSubjectToClass(classId, {
        name: courses.find((c) => c._id === newSubject.subjectId)?.name || "",
      });
    },
    onSuccess: () => {
      toast.success("Subject added to class successfully");
      queryClient.invalidateQueries({ queryKey: ["class", classId] });
      setNewSubject({ subjectId: "", teacherId: "" });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to add subject to class");
    },
  });

  // Assign teacher to subject mutation
  const assignTeacherMutation = useMutation({
    mutationFn: async (data: { subjectName: string; teacherId: string }) => {
      console.log("Assigning teacher mutation called with:", data);
      return assignTeacherToSubject(classId, data.subjectName, {
        teacherId: data.teacherId,
      });
    },
    onSuccess: (response) => {
      console.log("Teacher assignment successful:", response);
      toast.success("Teacher assigned to subject successfully");
      queryClient.invalidateQueries({ queryKey: ["class", classId] });
    },
    onError: (error: any) => {
      console.error("Teacher assignment error:", error);
      console.error("Error details:", {
        message: error?.message,
        response: error?.response,
        data: error?.response?.data,
      });
      toast.error(
        error?.response?.data?.message ||
          error.message ||
          "Failed to assign teacher to subject",
      );
    },
  });

  // Set class teacher mutation
  const setClassTeacherMutation = useMutation({
    mutationFn: async (teacherId: string) => {
      return setClassTeacher(classId, teacherId);
    },
    onSuccess: () => {
      toast.success("Class teacher assigned successfully");
      queryClient.invalidateQueries({ queryKey: ["class", classId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to assign class teacher");
    },
  });

  // Add schedule period mutation
  const addPeriodMutation = useMutation({
    mutationFn: async () => {
      return addPeriodToSchedule(classId, {
        day: newPeriod.day as
          | "monday"
          | "tuesday"
          | "wednesday"
          | "thursday"
          | "friday"
          | "saturday"
          | "sunday",
        startTime: newPeriod.startTime,
        endTime: newPeriod.endTime,
        subjectName:
          courses.find((c) => c._id === newPeriod.subjectId)?.name || "",
        teacherId: newPeriod.teacherId,
        studentIds: newPeriod.studentIds,
      });
    },
    onSuccess: () => {
      toast.success("Schedule period added successfully");
      queryClient.invalidateQueries({ queryKey: ["class-schedule", classId] });
      setNewPeriod({
        day: "",
        startTime: "",
        endTime: "",
        subjectId: "",
        teacherId: "",
        studentIds: [],
      });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to add schedule period");
    },
  });

  const handleAddStudents = () => {
    if (selectedStudents.length === 0) {
      toast.error("Please select at least one student");
      return;
    }
    addStudentsMutation.mutate(selectedStudents);
  };

  const handleRemoveStudent = (studentId: string) => {
    removeStudentsMutation.mutate([studentId]);
  };

  const handleAddSubject = () => {
    if (!newSubject.subjectId) {
      toast.error("Please select a subject");
      return;
    }
    addSubjectMutation.mutate();
  };

  const handleAssignTeacher = (subjectName: string, teacherId: string) => {
    console.log("handleAssignTeacher called:", {
      subjectName,
      teacherId,
      classId,
    });
    if (!subjectName || !teacherId) {
      console.error("Missing required parameters:", { subjectName, teacherId });
      toast.error("Missing subject name or teacher ID");
      return;
    }
    assignTeacherMutation.mutate({ subjectName, teacherId });
  };

  const handleAddPeriod = () => {
    if (
      !newPeriod.day ||
      !newPeriod.startTime ||
      !newPeriod.endTime ||
      !newPeriod.subjectId
    ) {
      toast.error("Please fill in all required fields");
      return;
    }
    addPeriodMutation.mutate();
  };

  if (!isOpen) return null;

  const tabs = [
    { id: "overview", label: "Overview", icon: BookOpen },
    { id: "students", label: "Students", icon: Users },
    { id: "teachers", label: "Teachers", icon: User },
    { id: "schedule", label: "Schedule", icon: Calendar },
  ];

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity z-[9998]"
          onClick={onClose}
        />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full relative z-[10000]">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <GraduationCap className="h-6 w-6 text-blue-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Manage Class: {classData?.name || "Loading..."}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {classData?.grade}{" "}
                    {classData?.section && `- Section ${classData.section}`}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="mt-6">
              <nav className="flex space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as TabType)}
                      className={`flex items-center px-1 py-2 text-sm font-medium border-b-2 ${
                        activeTab === tab.id
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-4 pb-4 sm:p-6 max-h-[70vh] overflow-y-auto">
            {classLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : (
              <>
                {activeTab === "overview" && (
                  <OverviewTab
                    classData={classData}
                    teachers={teachers}
                    onSetClassTeacher={(teacherId) =>
                      setClassTeacherMutation.mutate(teacherId)
                    }
                  />
                )}

                {activeTab === "students" && (
                  <StudentsTab
                    classData={classData}
                    availableStudents={availableStudents}
                    selectedStudents={selectedStudents}
                    onSelectedStudentsChange={setSelectedStudents}
                    onAddStudents={handleAddStudents}
                    onRemoveStudent={handleRemoveStudent}
                    isLoading={
                      addStudentsMutation.isPending ||
                      removeStudentsMutation.isPending
                    }
                  />
                )}

                {activeTab === "teachers" && (
                  <TeachersTab
                    classData={classData}
                    teachers={teachers}
                    courses={courses}
                    newSubject={newSubject}
                    onNewSubjectChange={setNewSubject}
                    onAddSubject={handleAddSubject}
                    onAssignTeacher={handleAssignTeacher}
                    isLoading={
                      addSubjectMutation.isPending ||
                      assignTeacherMutation.isPending
                    }
                  />
                )}

                {activeTab === "schedule" && (
                  <ScheduleTab
                    schedule={schedule}
                    courses={courses}
                    teachers={teachers}
                    classData={classData}
                    allStudents={students}
                    newPeriod={newPeriod}
                    onNewPeriodChange={setNewPeriod}
                    onAddPeriod={handleAddPeriod}
                    isLoading={addPeriodMutation.isPending}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab: React.FC<{
  classData: any;
  teachers: any[];
  onSetClassTeacher: (teacherId: string) => void;
}> = ({ classData, teachers, onSetClassTeacher }) => {
  const [selectedClassTeacher, setSelectedClassTeacher] = useState("");

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Basic Information</h4>
          <div className="space-y-2">
            <div>
              <span className="text-sm text-gray-500">Name:</span>
              <span className="ml-2 text-sm font-medium">
                {classData?.name}
              </span>
            </div>
            <div>
              <span className="text-sm text-gray-500">Grade:</span>
              <span className="ml-2 text-sm font-medium">
                {classData?.grade}
              </span>
            </div>
            {classData?.section && (
              <div>
                <span className="text-sm text-gray-500">Section:</span>
                <span className="ml-2 text-sm font-medium">
                  {classData.section}
                </span>
              </div>
            )}
            <div>
              <span className="text-sm text-gray-500">Capacity:</span>
              <span className="ml-2 text-sm font-medium">
                {classData?.capacity || "N/A"}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Statistics</h4>
          <div className="space-y-2">
            <div>
              <span className="text-sm text-gray-500">Total Students:</span>
              <span className="ml-2 text-sm font-medium">
                {classData?.students?.length || 0}
              </span>
            </div>
            <div>
              <span className="text-sm text-gray-500">Subjects:</span>
              <span className="ml-2 text-sm font-medium">
                {classData?.subjects?.length || 0}
              </span>
            </div>
            <div>
              <span className="text-sm text-gray-500">Status:</span>
              <span className="ml-2 text-sm font-medium">
                {classData?.status || "Active"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Class Teacher Assignment */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Class Teacher</h4>
        <div className="flex items-center space-x-4">
          <select
            value={selectedClassTeacher}
            onChange={(e) => setSelectedClassTeacher(e.target.value)}
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Select a teacher...</option>
            {teachers.map((teacher) => (
              <option key={teacher._id} value={teacher._id}>
                {teacher.firstName} {teacher.lastName}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              if (selectedClassTeacher) {
                onSetClassTeacher(selectedClassTeacher);
                setSelectedClassTeacher("");
              }
            }}
            disabled={!selectedClassTeacher}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Assign
          </button>
        </div>
        {classData?.classTeacher && (
          <p className="mt-2 text-sm text-gray-600">
            Current class teacher: {classData.classTeacher.firstName}{" "}
            {classData.classTeacher.lastName}
          </p>
        )}
      </div>
    </div>
  );
};

// Students Tab Component
const StudentsTab: React.FC<{
  classData: any;
  availableStudents: any[];
  selectedStudents: string[];
  onSelectedStudentsChange: (students: string[]) => void;
  onAddStudents: () => void;
  onRemoveStudent: (studentId: string) => void;
  isLoading: boolean;
}> = ({
  classData,
  availableStudents,
  selectedStudents,
  onSelectedStudentsChange,
  onAddStudents,
  onRemoveStudent,
  isLoading,
}) => {
  return (
    <div className="space-y-6">
      {/* Add Students Section */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Add Students</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Students to Add
            </label>
            <div className="max-h-40 overflow-y-auto border rounded-md p-2">
              {availableStudents.length === 0 ? (
                <p className="text-sm text-gray-500">No available students</p>
              ) : (
                availableStudents.map((student) => (
                  <label
                    key={student._id}
                    className="flex items-center space-x-2 p-2 hover:bg-gray-100"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onSelectedStudentsChange([
                            ...selectedStudents,
                            student._id,
                          ]);
                        } else {
                          onSelectedStudentsChange(
                            selectedStudents.filter((id) => id !== student._id),
                          );
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">
                      {student.firstName} {student.lastName} -{" "}
                      {student.studentId}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
          <button
            onClick={onAddStudents}
            disabled={isLoading || selectedStudents.length === 0}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading
              ? "Adding..."
              : `Add ${selectedStudents.length} Students`}
          </button>
        </div>
      </div>

      {/* Current Students Section */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">
          Current Students ({classData?.students?.length || 0})
        </h4>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {classData?.students?.length === 0 ? (
            <p className="text-sm text-gray-500">No students in this class</p>
          ) : (
            classData?.students?.map((student: any) => (
              <div
                key={student._id}
                className="flex items-center justify-between p-2 bg-white rounded border"
              >
                <div>
                  <span className="text-sm font-medium">
                    {student.firstName} {student.lastName}
                  </span>
                  <span className="text-sm text-gray-500 ml-2">
                    - {student.studentId}
                  </span>
                </div>
                <button
                  onClick={() => onRemoveStudent(student._id)}
                  disabled={isLoading}
                  className="text-red-600 hover:text-red-800 disabled:text-gray-400"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Teachers Tab Component
const TeachersTab: React.FC<{
  classData: any;
  teachers: any[];
  courses: any[];
  newSubject: { subjectId: string; teacherId: string };
  onNewSubjectChange: (subject: {
    subjectId: string;
    teacherId: string;
  }) => void;
  onAddSubject: () => void;
  onAssignTeacher: (subjectName: string, teacherId: string) => void;
  isLoading: boolean;
}> = ({
  classData,
  teachers,
  courses,
  newSubject,
  onNewSubjectChange,
  onAddSubject,
  onAssignTeacher,
  isLoading,
}) => {
  return (
    <div className="space-y-6">
      {/* Add Subject Section */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Add Subject</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <select
              value={newSubject.subjectId}
              onChange={(e) =>
                onNewSubjectChange({ ...newSubject, subjectId: e.target.value })
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select a subject...</option>
              {courses.map((course) => (
                <option key={course._id} value={course._id}>
                  {course.name}({course.level})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teacher (Optional)
            </label>
            <select
              value={newSubject.teacherId}
              onChange={(e) =>
                onNewSubjectChange({ ...newSubject, teacherId: e.target.value })
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select a teacher...</option>
              {teachers.map((teacher) => (
                <option key={teacher._id} value={teacher._id}>
                  {teacher.firstName} {teacher.lastName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={onAddSubject}
              disabled={isLoading || !newSubject.subjectId}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isLoading ? "Adding..." : "Add Subject"}
            </button>
          </div>
        </div>
      </div>

      {/* Current Subjects & Teachers */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">
          Subjects & Teachers ({classData?.subjects?.length || 0})
        </h4>
        <div className="space-y-3">
          {classData?.subjects?.length === 0 ? (
            <p className="text-sm text-gray-500">
              No subjects assigned to this class
            </p>
          ) : (
            classData?.subjects?.map((subject: any, index: number) => (
              <div key={index} className="bg-white rounded border p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-gray-900">
                      {subject.name}
                    </h5>
                    <p className="text-sm text-gray-500">
                      Teacher:{" "}
                      {subject.teacher
                        ? `${subject.teacher.firstName} ${subject.teacher.lastName}`
                        : "Not assigned"}
                    </p>
                  </div>
                  {!subject.teacher && (
                    <select
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (e.target.value) {
                          console.log(
                            "Assigning teacher:",
                            e.target.value,
                            "to subject:",
                            subject.name,
                          );
                          onAssignTeacher(subject.name, e.target.value);
                          e.target.value = ""; // Reset the dropdown
                        }
                      }}
                      className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      disabled={isLoading}
                    >
                      <option value="">Assign teacher...</option>
                      {teachers.map((teacher) => (
                        <option key={teacher._id} value={teacher._id}>
                          {teacher.firstName} {teacher.lastName}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Schedule Tab Component
const ScheduleTab: React.FC<{
  schedule: any[];
  courses: any[];
  teachers: any[];
  classData: any;
  allStudents: any[];
  newPeriod: {
    day: string;
    startTime: string;
    endTime: string;
    subjectId: string;
    teacherId: string;
    studentIds: string[];
  };
  onNewPeriodChange: (period: any) => void;
  onAddPeriod: () => void;
  isLoading: boolean;
}> = ({
  schedule,
  courses,
  teachers,
  classData,
  allStudents,
  newPeriod,
  onNewPeriodChange,
  onAddPeriod,
  isLoading,
}) => {
  const days = ["monday", "tuesday", "wednesday", "thursday", "friday"];

  // Students enrolled in the selected course (filters from allStudents already fetched)
  const courseStudents = newPeriod.subjectId
    ? allStudents.filter(
        (student: any) =>
          Array.isArray(student.courses) &&
          student.courses.includes(newPeriod.subjectId),
      )
    : [];

  const toggleStudentId = (studentId: string) => {
    const current = newPeriod.studentIds || [];
    const updated = current.includes(studentId)
      ? current.filter((id) => id !== studentId)
      : [...current, studentId];
    onNewPeriodChange({ ...newPeriod, studentIds: updated });
  };

  return (
    <div className="space-y-6">
      {/* Add Period Section */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Add Schedule Period</h4>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Day
            </label>
            <select
              value={newPeriod.day}
              onChange={(e) =>
                onNewPeriodChange({ ...newPeriod, day: e.target.value })
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select day...</option>
              {days.map((day) => (
                <option key={day} value={day}>
                  {day.charAt(0).toUpperCase() + day.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Time
            </label>
            <input
              type="time"
              value={newPeriod.startTime}
              onChange={(e) =>
                onNewPeriodChange({ ...newPeriod, startTime: e.target.value })
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Time
            </label>
            <input
              type="time"
              value={newPeriod.endTime}
              onChange={(e) =>
                onNewPeriodChange({ ...newPeriod, endTime: e.target.value })
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <select
              value={newPeriod.subjectId}
              onChange={(e) =>
                onNewPeriodChange({
                  ...newPeriod,
                  subjectId: e.target.value,
                  // Clear student selection when course changes
                  studentIds: [],
                })
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select subject...</option>
              {courses.map((course) => (
                <option key={course._id} value={course._id}>
                  {course.name}({course.level})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teacher
            </label>
            <select
              value={newPeriod.teacherId}
              onChange={(e) =>
                onNewPeriodChange({ ...newPeriod, teacherId: e.target.value })
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select teacher...</option>
              {teachers.map((teacher) => (
                <option key={teacher._id} value={teacher._id}>
                  {teacher.firstName} {teacher.lastName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Student Assignment */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Assign to Students
          </label>

          {/* All students toggle */}
          <label className="flex items-center gap-2 cursor-pointer mb-2 select-none">
            <input
              type="checkbox"
              className="w-4 h-4 accent-blue-600"
              checked={!newPeriod.studentIds?.length}
              onChange={() =>
                onNewPeriodChange({ ...newPeriod, studentIds: [] })
              }
            />
            <span className="text-sm font-medium text-gray-700">
              All enrolled students (default)
            </span>
          </label>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-gray-100 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {newPeriod.subjectId
                  ? "Students enrolled in this course:"
                  : "Select a subject first to list students"}
              </span>
              {!!newPeriod.studentIds?.length && (
                <span className="text-xs font-semibold text-blue-600">
                  {newPeriod.studentIds.length} selected
                </span>
              )}
            </div>
            <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
              {!newPeriod.subjectId ? (
                <p className="px-3 py-3 text-xs text-gray-400 text-center">
                  Choose a subject above to see enrolled students.
                </p>
              ) : courseStudents.length === 0 ? (
                <p className="px-3 py-3 text-xs text-gray-400 text-center">
                  No students are enrolled in this course yet.
                </p>
              ) : (
                courseStudents.map((student: any) => (
                  <label
                    key={student._id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-blue-600"
                      checked={
                        newPeriod.studentIds?.includes(student._id) ?? false
                      }
                      onChange={() => toggleStudentId(student._id)}
                    />
                    <span className="text-sm text-gray-700 flex-1 truncate">
                      {student.userInfo?.firstName} {student.userInfo?.lastName}
                    </span>
                    <span className="text-xs text-gray-400 shrink-0">
                      {student.studentId}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={onAddPeriod}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading ? "Adding..." : "Add Period"}
          </button>
        </div>
      </div>

      {/* Schedule Display */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Weekly Schedule</h4>
        <div className="grid grid-cols-1 gap-4">
          {days.map((day) => (
            <div key={day} className="bg-white rounded border p-3">
              <h5 className="font-medium text-gray-800 mb-2 capitalize">
                {day}
              </h5>
              <div className="space-y-2">
                {schedule
                  .filter((period: any) => period.day?.toLowerCase() === day)
                  .sort((a: any, b: any) =>
                    (a.startTime || "").localeCompare(b.startTime || ""),
                  )
                  .map((period: any, index: number) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-2 bg-blue-50 rounded"
                    >
                      <div>
                        <span className="text-sm font-medium">
                          {period.subject}
                        </span>
                        <span className="text-sm text-gray-500 ml-2">
                          {period.startTime} - {period.endTime}
                        </span>
                        <span className="ml-2 text-xs">
                          {period.studentIds?.length > 0 ? (
                            <span className="text-indigo-600 font-medium">
                              ({period.studentIds.length} students)
                            </span>
                          ) : (
                            <span className="text-green-600 font-medium">
                              (All students)
                            </span>
                          )}
                        </span>
                      </div>
                      <span className="text-sm text-gray-600">
                        {period.teacher?.firstName} {period.teacher?.lastName}
                      </span>
                    </div>
                  ))}
                {schedule.filter(
                  (period: any) => period.day?.toLowerCase() === day,
                ).length === 0 && (
                  <p className="text-sm text-gray-500">No periods scheduled</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClassManagementModal;
